import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { apiRequest } from "../../../../shared/api/client";
import type { SessionUser } from "../../../../shared/auth/access";
import type { Rfq, Candidate } from "../../domain/rfq-types";
import { useProcurementScope } from "../shared/useProcurementScope";
import { rfqUiMessage } from "./rfq-presentation";

type Scope = ReturnType<typeof useProcurementScope>;
type State = { scope: Scope; rfq: Rfq | null; candidates: Candidate[]; selected: string[]; loading: boolean; error: string };
const emptyState = (scope: Scope): State => ({ scope, rfq: null, candidates: [], selected: [], loading: true, error: "" });

export function useRfqData({ requestNo, user, notify }: { requestNo: string; user: SessionUser; notify: (message: string) => void }) {
  const scope = useProcurementScope(requestNo, user, notify);
  const [state, setState] = useState(() => emptyState(scope));
  const versions = useRef({ load: 0, rfq: 0, candidates: 0, selected: 0 });
  const setRfq: Dispatch<SetStateAction<Rfq | null>> = useCallback(value => {
    if (!scope.isCurrent()) return;
    versions.current.rfq++;
    setState(current => {
      const previous = current.scope === scope ? current : emptyState(scope);
      return { ...previous, rfq: typeof value === "function" ? value(previous.rfq) : value };
    });
  }, [scope]);
  const setCandidates: Dispatch<SetStateAction<Candidate[]>> = useCallback(value => {
    if (!scope.isCurrent()) return;
    versions.current.candidates++;
    setState(current => {
      const previous = current.scope === scope ? current : emptyState(scope);
      return { ...previous, candidates: typeof value === "function" ? value(previous.candidates) : value };
    });
  }, [scope]);
  const setSelected: Dispatch<SetStateAction<string[]>> = useCallback(value => {
    if (!scope.isCurrent()) return;
    versions.current.selected++;
    setState(current => {
      const previous = current.scope === scope ? current : emptyState(scope);
      return { ...previous, selected: typeof value === "function" ? value(previous.selected) : value };
    });
  }, [scope]);
  const setError: Dispatch<SetStateAction<string>> = useCallback(value => {
    if (!scope.isCurrent()) return;
    setState(current => {
      const previous = current.scope === scope ? current : emptyState(scope);
      return { ...previous, error: typeof value === "function" ? value(previous.error) : value };
    });
  }, [scope]);
  const load = useCallback(async () => {
    if (!scope.isCurrent()) return;
    const snapshot = { ...versions.current, load: ++versions.current.load };
    const isCurrent = () => scope.isCurrent() && snapshot.load === versions.current.load;
    setState(current => ({ ...(current.scope === scope ? current : emptyState(scope)), loading: true, error: "" }));
    try {
      const rows = await apiRequest<Array<{ id: string }>>(`/api/requests/${encodeURIComponent(requestNo)}/rfqs`);
      if (!isCurrent()) return;
      let rfq: Rfq | null = null;
      let candidates: Candidate[] = [];
      if (rows[0]) {
        rfq = await apiRequest<Rfq>(`/api/rfqs/${rows[0].id}`);
        if (!isCurrent()) return;
        if (!rfq || rfq.id !== rows[0].id) throw new Error("Teklif talebi yanıtı seçilen kayıtla uyuşmuyor.");
        const confirmedRfq = rfq;
        if (snapshot.rfq === versions.current.rfq) {
          setState(current => ({ ...(current.scope === scope ? current : emptyState(scope)), rfq: confirmedRfq }));
        }
        candidates = await apiRequest<Candidate[]>(`/api/rfqs/${rows[0].id}/candidates`);
      }
      if (!isCurrent()) return;
      const selected = (rfq?.selections ?? []).map(item => item.supplier_id ?? item.supplierId ?? "");
      setState(current => {
        const previous = current.scope === scope ? current : emptyState(scope);
        return { ...previous, loading: false,
          rfq: snapshot.rfq === versions.current.rfq ? rfq : previous.rfq,
          candidates: snapshot.candidates === versions.current.candidates ? candidates : previous.candidates,
          selected: snapshot.selected === versions.current.selected ? selected : previous.selected,
        };
      });
    } catch (value) {
      if (isCurrent()) setError(rfqUiMessage(value, "Teklif Talebi çalışma alanı yüklenemedi."));
    } finally {
      if (isCurrent()) setState(current => ({ ...current, loading: false }));
    }
  }, [requestNo, scope, setError]);
  useEffect(() => { void load(); }, [load]);
  const visible = state.scope === scope ? state : emptyState(scope);
  return { ...visible, scope, notify: scope.notify, setRfq, setCandidates, setSelected, setError, load };
}
